/**
 * ⭐⭐ `AW24` · **La mitad que el negocio inicia: mandar una PLANTILLA** a la persona
 * que se está impersonando.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ① MANEJA EL MOTOR REAL. NO DIBUJA UN ENVÍO. · `ADR-0229`
 * ════════════════════════════════════════════════════════════════════════════
 *
 * La implementación cómoda —un botón que agrega una burbuja «plantilla enviada» y
 * no escribe nada— es **la prohibición literal de `ADR-0040 §②`**: *«se puede fingir
 * lo que no promete nada que el sistema no vaya a cumplir. NO SE PUEDE FINGIR UNA
 * OBLIGACIÓN.»* El daño acá es un operador que **firma un caso de QA sobre un envío
 * que nunca ocurrió**.
 *
 * ⭐ **Y se puede hacer bien porque el camino existe entero**, medido el 28/8/2026:
 * `RecipientRef = string` (un id de participante), `recipients` es una lista de
 * ellos, `modules/dispatch/src/motor.ts:195` construye `kind: "plantilla"`, y
 * `apps/job-worker/src/index.ts:93` lo levanta. ⇒ **un envío de UN destinatario es
 * un caso legítimo del contrato**, no una superficie nueva. Es el mismo argumento
 * con el que `ADR-0198` reusó `POST /entrada`.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ② ESTO ESCRIBE UNA FILA REAL, Y POR ESO SE PIDE DOS VECES
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `PRODUCT.md` §4: *«lo irreversible se pide dos veces; lo destructivo no es lo más
 * llamativo»*. El `job-worker` va a levantar la fila.
 *
 * ⛔ **Un «¿estás seguro?» genérico NO cumple.** Lo que hace verificable la decisión
 * es ver **A QUIÉN y CON QUÉ**, que es exactamente el par que el operador puede
 * equivocar —eligió otro participante, escribió otra plantilla—. La confirmación los
 * nombra.
 *
 * ⚠️⚠️ **El riesgo que la confirmación NO cubre, declarado:** con padrón real esto le
 * manda una plantilla a **una persona de verdad**. Lo único que hoy lo hace tolerable
 * es lo mismo que `ADR-0221`: la base del UAT está vacía. **Antes del primer padrón
 * real esta columna necesita una guarda que no está construida.** Dueño: el TL.
 *
 * ⛔ ③ **La saliente NO se dibuja al disparar.** Aparece —o no— por el mismo sondeo
 * de `GET /entregas` que ya existe, **cuando el motor la escriba**. Entre el disparo
 * y la entrega hay un tiempo real, así que dibujarla antes sería la pantalla
 * afirmando algo que todavía no pasó.
 *
 * ⚠️⚠️ **[VENCIDO EL 1/9/2026 — LA SEGUNDA ORACIÓN DE ③ ES FALSA, Y LO ERA DESDE QUE SE
 * ESCRIBIÓ. LA PRIMERA Y LA TERCERA SIGUEN ENTERAS.]** No se borra: el error de
 * razonamiento es la parte útil. Se creyó que «no dibujarla acá» y «aparece por el
 * sondeo» eran las dos mitades de la misma decisión, y **son dos afirmaciones
 * independientes**: la primera es sobre lo que ESTA pantalla no hace —cierta— y la
 * segunda es sobre lo que OTRO camino sí hace —nunca lo hizo—.
 *
 * ⛔ **UNA PLANTILLA NO VA A APARECER EN EL HILO CON EL ÁRBOL DE HOY.** La cadena,
 * medida entera el 1/9/2026 y no supuesta:
 *
 *   ① `apps/bot-web/src/compartido/entrega.ts:162`
 *        `if (message.content.kind !== "texto-libre") return { ok:false, … }`
 *      ⇒ el transporte que SÍ atribuye conversación **rechaza toda plantilla**, y lo
 *        hace **antes** de llegar a `deps.conversaciones.registrar(…)`, que está en
 *        la `:181` — **19 líneas después**. Nunca se ejecuta para una plantilla.
 *   ② `grep -rn 'conversaciones\.registrar' apps modules` → **DOS** llamadas:
 *        `apps/bot-web/src/registrar.ts:123`  (la ENTRANTE)
 *        `apps/bot-web/src/compartido/entrega.ts:181`  (la saliente, sólo texto libre)
 *      ⇒ **no hay un tercer escritor de la tabla de conversación.**
 *   ③ El camino que sí acepta plantillas es `apps/job-worker/src/libro-de-salida.ts`,
 *      que escribe `channel.outbound_deliveries` y **declara por escrito que no
 *      atribuye nada** (`:70`): *«un libro de transporte no atribuye nada: anota que
 *      salió»*, y por eso ⑤ dice que **no necesita `ParticipantDirectory`**, que es
 *      justo la pieza con la que `entrega.ts` arma el `id_usuario`.
 *   ④ `GET /entregas` —la fuente del sondeo del hilo— lee la tabla de CONVERSACIÓN
 *      (`apps/bot-web/src/entregas.ts` importa `contracts/conversation` y filtra
 *      `f.es_sistema`), **no** `channel.outbound_deliveries`.
 *
 * ⇒ ①+②+③+④: la plantilla sale, deja rastro **en otra tabla**, y el sondeo mira una
 * tabla en la que **no hay fila**. La promesa no es «improbable»: es **imposible**.
 *
 * ⛔⛔ **ESTO NO SE ARREGLA ACÁ, Y NO ES PEREZA.** Hacer que una plantilla atribuya
 * conversación es `CH5` —**diferida con `defer:` formal hasta el 31/10/2026**— más
 * `AW24`. Lo único que estaba mal HOY era **la promesa**, y es lo único que se tocó:
 * el texto de pantalla, abajo. **`ADR-0040 §②`: se puede fingir lo que no promete
 * nada que el sistema no vaya a cumplir; NO SE PUEDE FINGIR UNA OBLIGACIÓN** — y una
 * pantalla que le dice al operador «esperá que aparezca» lo deja mirando un hilo que
 * no va a cambiar nunca, que es la misma familia de daño que ① existe para prohibir:
 * **un operador que firma un caso de QA sobre algo que no ocurrió.**
 */

import { useEffect, useState } from "react"
import type { JSX } from "react"
import type { TemplateContent } from "@contracts/channel/index.ts"
import type { BordeEnvios } from "../envios/borde-envios.ts"
import {
  ESTADO_APROBADA_EN_META,
  SIN_CATALOGO_DE_PLANTILLAS,
} from "../plantillas/borde-plantillas.ts"
import type { CargarPlantillas, PlantillaDelBrazo } from "../plantillas/borde-plantillas.ts"
import type { Elegido } from "./selector-participante.tsx"

/**
 * `AW46` · El rótulo de «todavía no elegí ninguna». Su `value` es la cadena
 * vacía, que es lo que `nombreLimpio === ""` mide.
 */
const OPCION_SIN_PLANTILLA = "— Elegí una plantilla aprobada —"

/** Lo que quedó del último disparo, para poder decirlo sin inventarlo. */
type Rastro =
  | { readonly caso: "ninguno" }
  | { readonly caso: "encolado"; readonly plantilla: string; readonly aQuien: string }
  | { readonly caso: "fallo"; readonly mensaje: string }

export function ColumnaDePlantilla({
  borde,
  elegido,
  cargarPlantillas = SIN_CATALOGO_DE_PLANTILLAS,
}: {
  readonly borde: BordeEnvios
  readonly elegido: Elegido
  /**
   * ⭐⭐ `AW46` · La fuente del catálogo del brazo, con el brazo ya resuelto por
   * quien monta la ruta (`App.tsx` §`useCargarPlantillas`).
   *
   * Va por PROP como los otros bordes del simulador —y con default vacío— para
   * que un examen lo pueda reemplazar por un doble sin montar ningún proveedor, y
   * para que una pantalla sin brazo declarado ofrezca CERO plantillas en vez de
   * una lista inventada.
   */
  readonly cargarPlantillas?: CargarPlantillas
}): JSX.Element {
  const [plantilla, setPlantilla] = useState("")
  const [confirmando, setConfirmando] = useState(false)
  const [enCurso, setEnCurso] = useState(false)
  const [rastro, setRastro] = useState<Rastro>({ caso: "ninguno" })
  const [plantillasDelBrazo, setPlantillasDelBrazo] = useState<readonly PlantillaDelBrazo[]>([])
  const [errorPlantillas, setErrorPlantillas] = useState<string | null>(null)

  /**
   * El catálogo se lee UNA vez al montar la columna. Un fallo acá es del selector
   * y no del disparo: va a `errorPlantillas`, nunca al `rastro`.
   */
  useEffect(() => {
    let cancelado = false

    void cargarPlantillas()
      .then((traidas) => {
        if (cancelado) return
        setPlantillasDelBrazo(traidas)
      })
      .catch((causa: unknown) => {
        if (cancelado) return
        setErrorPlantillas(causa instanceof Error ? causa.message : String(causa))
      })

    return () => {
      cancelado = true
    }
  }, [cargarPlantillas])

  /**
   * ⭐⭐ **SÓLO LAS APROBADAS DEL BRAZO** (PRD L323-328 y L786-788).
   *
   * ⛔ Mira `approvalStatus` contra `ESTADO_APROBADA_EN_META`, el único literal de
   * Meta que este producto nombra como símbolo y que `tsc` sostiene contra
   * `contracts/channel` (`ADR-0247`). Todo lo demás queda afuera **sin enumerar el
   * resto del vocabulario**, que es de Meta y ninguna fuente del repo lo tiene:
   * `ADR-0008` ya decidió la dirección segura del error — **la ausencia de dato no
   * es una aprobación**.
   */
  const plantillasAprobadas = plantillasDelBrazo.filter(
    (candidata) => candidata.approvalStatus === ESTADO_APROBADA_EN_META,
  )

  const nombreLimpio = plantilla.trim()

  /**
   * La plantilla ELEGIDA del catálogo, por nombre. El `<select>` guarda el nombre
   * como `value`, así que éste es el único dato con el que se puede volver al
   * registro — y de ahí salen `language` y `variables`, que no se inventan.
   */
  const elegida = plantillasAprobadas.find((candidata) => candidata.name === nombreLimpio) ?? null

  /**
   * ⛔⛔ **FALLA CERRADA SI LA PLANTILLA TIENE VARIABLES.** El simulador NO sabe con
   * qué dato llenarlas: `TemplateContent.parameters` sólo admite `"nombre"` o
   * `"telefono"`, y elegir uno por la plantilla sería decidir por `F4` (el relleno
   * posicional es de Meta y del motor). Con variables declaradas el botón queda
   * deshabilitado y el texto fijo de abajo dice por qué — nunca se mapea cada
   * variable a un valor/fuente para «poder enviar igual», que sería inventar el dato.
   */
  const tieneVariables = elegida !== null && elegida.variables.length > 0

  const listo =
    elegido !== null &&
    elegida !== null &&
    nombreLimpio !== "" &&
    !tieneVariables &&
    !enCurso

  async function disparar(): Promise<void> {
    if (elegido === null || elegida === null || tieneVariables) return
    setEnCurso(true)
    try {
      const template: TemplateContent = {
        kind: "plantilla",
        name: nombreLimpio,
        language: elegida.language,
        parameters: [],
      }
      await borde.crear({
        /*
         * ⭐ El nombre y la etiqueta DECLARAN que salieron del simulador: un envío de
         * prueba tiene que ser distinguible de una campaña **desde cualquier
         * listado**, sin abrirlo. Es la obligación que `ADR-0196` §Consecuencias
         * hereda de `ADR-0135` para los casos de QA, aplicada al envío.
         */
        name: `[simulador] ${nombreLimpio}`,
        template,
        // ⛔ EXACTAMENTE un id: el de la persona impersonada. Nunca un lote.
        recipients: [elegido.id],
        audienceLabel: "1 destinatario (simulador)",
        // Inmediato: el `job-worker` lo levanta en su próximo barrido.
        scheduledFor: null,
      })
      setRastro({ caso: "encolado", plantilla: nombreLimpio, aQuien: elegido.nombre })
      setConfirmando(false)
      setPlantilla("")
    } catch (causa) {
      setRastro({ caso: "fallo", mensaje: causa instanceof Error ? causa.message : String(causa) })
      setConfirmando(false)
    } finally {
      setEnCurso(false)
    }
  }

  return (
    <aside className="sim__plantilla" aria-labelledby="sim-plantilla-titulo">
      <h2 id="sim-plantilla-titulo" className="sim__rotulo">
        Enviar una plantilla
      </h2>

      <p className="sim__ayuda">
        Es la mitad que inicia el negocio, y la que <strong>abre la ventana de 24 h</strong>. Va por
        el motor real de envíos, acotado a la persona elegida.
      </p>

      <div className="sim__campo">
        <label htmlFor="sim-plantilla-nombre">Nombre de la plantilla</label>
        <select
          id="sim-plantilla-nombre"
          value={plantilla}
          onChange={(e) => {
            setPlantilla(e.target.value)
            setConfirmando(false)
          }}
        >
          {/*
            La opción vacía existe para que «todavía no elegí» sea un estado
            alcanzable: sin ella el navegador deja preseleccionada la primera y el
            operador dispara la que el orden le puso adelante.
          */}
          <option value="">{OPCION_SIN_PLANTILLA}</option>
          {plantillasAprobadas.map((candidata) => (
            /*
             * El VALOR es el nombre y nada más: es lo único que el envío guarda
             * (`TemplateContent = { kind, name }`). El idioma se MUESTRA porque la
             * misma plantilla existe una vez por idioma del lado de Meta.
             */
            <option key={`${candidata.name}·${candidata.language}`} value={candidata.name}>
              {candidata.name} · {candidata.language}
            </option>
          ))}
        </select>
        <p className="sim__ayuda">
          {/*
            ⚠️⚠️ **[REDACTADO EL 1/9/2026 POR `AW46` — NO BORRADO (`ADR-0082`).]**

            El cartel decía, textual (`ADR-0229`):

              «No se valida y no se puede: las plantillas viven en el WhatsApp Manager de cada brazo y no hay endpoint que las liste. Un nombre inexistente falla del lado del proveedor, y eso es el motor diciendo la verdad — no un defecto que esta capa pueda atrapar.»

            ⭐ **Era cierto cuando se escribió, y la mitad que dejó de serlo es UNA:
            ahora hay endpoint que las liste.** `CA10` declaró
            `TemplatesAdminApi.catalog(arm)` en `contracts/admin-api` (`ADR-0246`),
            medido el 1/9/2026 —`grep -c -i 'plantilla' contracts/admin-api/index.ts`
            pasó de 0 a 18 en el día—, y `AW46` lo consume por
            `src/plantillas/borde-plantillas.ts`.

            ⛔ **LO QUE SIGUE SIENDO CIERTO, y por eso el cartel se redacta y no se
            borra:** las plantillas SIGUEN viviendo en el WhatsApp Manager de cada
            brazo —este producto no las crea ni las edita— y **este selector sigue
            sin validar contra Meta**: ofrece lo que la última SINCRONIZACIÓN
            trajo. Una plantilla que dejó de estar aprobada después de esa
            sincronización sigue apareciendo acá, y ese envío falla del lado del
            proveedor exactamente como antes. La re-validación al ejecutar es
            `canSend`, del motor de envío, y `CH5` sigue diferida.
          */}
          Sale del catálogo <strong>sincronizado</strong> del brazo, y sólo las que Meta tiene como
          aprobadas. No se valida contra Meta en este momento: si dejó de estar aprobada desde la
          última sincronización, el envío falla del lado del proveedor.
        </p>
        {/*
          Un catálogo que no se pudo leer deja el selector vacío, y eso se DICE: un
          selector vacío y mudo se lee como «este brazo no tiene plantillas
          aprobadas», que es otra cosa.
        */}
        {errorPlantillas !== null && (
          <p className="simulador__error" role="alert">
            No se pudo leer el catálogo de plantillas del brazo. {errorPlantillas}
          </p>
        )}
      </div>

      {elegido === null && (
        <p className="simulador__vacio">Elegí primero a quién, en la columna de la izquierda.</p>
      )}

      {tieneVariables && (
        <p className="simulador__error" role="status">
          Esta plantilla declara variables y el simulador todavía no completa variables: elegí una
          plantilla sin variables para poder disparar.
        </p>
      )}

      {!confirmando ? (
        <button
          type="button"
          className="boton"
          disabled={!listo}
          onClick={() => setConfirmando(true)}
        >
          Enviar plantilla…
        </button>
      ) : (
        /*
         * ⛔⛔ La confirmación NOMBRA al destinatario y a la plantilla. Un «¿estás
         * seguro?» genérico no le da al operador nada que verificar, y el par
         * (a quién, con qué) es justo el que se puede equivocar.
         */
        <div className="sim__confirmar" role="alertdialog" aria-labelledby="sim-confirmar-texto">
          <p id="sim-confirmar-texto">
            Se va a encolar un envío <strong>real</strong> de la plantilla{" "}
            <code>{nombreLimpio}</code> a <strong>{elegido?.nombre}</strong>{" "}
            <code>{elegido?.direccion}</code>. Lo levanta el <code>job-worker</code>.
          </p>
          <div className="sim__renglon">
            <button
              type="button"
              className="boton boton--primario"
              disabled={enCurso}
              onClick={() => void disparar()}
            >
              Encolar el envío
            </button>
            <button type="button" className="boton" onClick={() => setConfirmando(false)}>
              No
            </button>
          </div>
        </div>
      )}

      {rastro.caso === "encolado" && (
        <p className="sim__rastro" role="status">
          {/*
            ⛔ Dice «se encoló», no «se envió». Lo que ocurrió es que se escribió la
            fila; la entrega la hace el `job-worker`. Decir «enviado» acá sería una
            confirmación que la base no escribió.

            ⚠️ **[CORREGIDO EL 1/9/2026.]** Este comentario decía, y el texto de
            pantalla prometía, que la entrega «va a aparecer en el hilo por el sondeo,
            cuando el motor la escriba». **Era falso, y no por un margen**: ver la
            cadena de cuatro mediciones en el encabezado del archivo (`⛔ ③`). El
            resumen: `entrega.ts:162` rechaza toda plantilla 19 líneas ANTES del único
            `conversaciones.registrar` saliente (`:181`), el camino que sí las acepta
            —`job-worker/libro-de-salida.ts`— escribe `channel.outbound_deliveries` y
            declara que NO atribuye conversación, y `GET /entregas` sondea la tabla de
            conversación. La fila existe; **en el hilo no**.

            ⭐ LO QUE NO SE PERDIÓ AL CORREGIRLO, porque es lo útil y es cierto: que la
            fila QUEDÓ ESCRITA y que el motor la va a entregar. Lo que se sacó es la
            única parte que el sistema no puede cumplir — y se sacó DICIÉNDOLO, no
            callándolo: un operador que no ve nada en el hilo y no sabe por qué va a
            reportar un defecto que no existe, o peor, va a esperar.
          */}
          Se encoló <code>{rastro.plantilla}</code> para {rastro.aQuien}. La fila quedó escrita y
          la va a entregar el <code>job-worker</code>.{" "}
          <strong>No la esperes en el hilo:</strong> una plantilla deja su rastro en{" "}
          <code>channel.outbound_deliveries</code>, y el hilo se sondea de la tabla de
          conversación, donde una saliente de plantilla no escribe fila.
        </p>
      )}

      {rastro.caso === "fallo" && (
        <p className="simulador__error" role="alert">
          No se pudo encolar: {rastro.mensaje}
        </p>
      )}
    </aside>
  )
}
